import * as React from 'react';

import { useApolloClient, useQuery } from '@apollo/client';

import { ReactFCC } from '@notino/react-styleguide';
import {
  GetImageSettingsQuery,
  GetRequestHeadersQuery,
} from '@notino/shared/definitions/types';
import {
  defaultProductCardMessages,
  IProductCardSettings,
  ProductCardSettingsProvider,
} from '@notino/web-libraries-product-card';

import getImageSettingsQuery from '@components/CdnImage/queries/imageSettings.graphql';
import { usePriceConfig } from '@components/PriceLabel/usePriceConfig';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import getRequestHeadersQuery from '../../../queries/requestHeaders.graphql';

const DEFAULT_PRICE_DECIMAL_PLACES = 2;

/**
 * Feeds the product card package from the shop settings the fragment already
 * loads. The package hooks read prices, CDN path and translations from here.
 */
export const ProductCardSettings: ReactFCC = ({ children }) => {
  const { dual_currency_price } = useFeatureFlags();
  const { cache } = useApolloClient();
  const { data: priceConfig } = usePriceConfig();
  const { data: imageSettings } = useQuery<GetImageSettingsQuery>(
    getImageSettingsQuery
  );

  const requestHeaders = cache.readQuery<GetRequestHeadersQuery>({
    query: getRequestHeadersQuery,
  })?.RequestHeaders;
  const secondaryCurrency = dual_currency_price
    ? requestHeaders?.secondaryCurrency
    : null;

  const { currentLocale, currentCurrency } = priceConfig ?? {};
  const imageDomainPath = imageSettings?.Settings?.imageDomainPath;
  const shopId = requestHeaders?.shopId ?? '';
  const isPrimaryCurrencyFirst = requestHeaders?.isPrimaryCurrencyFirst;

  const settings = React.useMemo<IProductCardSettings>(
    () => ({
      shopId,
      imageDomainPath: imageDomainPath ?? undefined,
      primaryCurrency: {
        code: currentCurrency?.code ?? '',
        symbol: currentCurrency?.symbol || currentLocale?.currency || '',
        priceFormat: currentLocale?.priceFormat ?? '',
        priceDecimalPlaces:
          currentLocale?.priceDecimalPlaces ?? DEFAULT_PRICE_DECIMAL_PLACES,
      },
      secondaryCurrency: secondaryCurrency
        ? {
            code: secondaryCurrency.code ?? '',
            symbol: secondaryCurrency.symbol ?? '',
            priceFormat: secondaryCurrency.priceFormat ?? '',
            priceDecimalPlaces:
              secondaryCurrency.priceDecimalPlaces ??
              DEFAULT_PRICE_DECIMAL_PLACES,
          }
        : undefined,
      isPrimaryCurrencyFirst: isPrimaryCurrencyFirst ?? undefined,
      messages: defaultProductCardMessages,
    }),
    [
      shopId,
      imageDomainPath,
      currentCurrency,
      currentLocale,
      secondaryCurrency,
      isPrimaryCurrencyFirst,
    ]
  );

  return (
    <ProductCardSettingsProvider settings={settings}>
      {children}
    </ProductCardSettingsProvider>
  );
};
